import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import useId from "react-use-uuid"
import styled, { keyframes } from "styled-components"
import { useTheme } from "@mui/material/styles"

import { Delaunay } from "d3-delaunay"
import { TweenLite } from "gsap"
import { generateArmBetweenPoints } from "../../utils/trig"

import _findIndex from "lodash/findIndex"
import _maxBy from "lodash/maxBy"
import _minBy from "lodash/minBy"

import CustomNode from "./CustomNode"
import { isValidColor } from "../../utils/color"

const Svg = styled.svg``

const GraphWrap = styled.div`
  width: 100%;
  height: 100%;
`

const pathTrailAnim = keyframes`
0% {
  stroke-dashoffset: 0;
}
100% {
  stroke-dashoffset: -12;
}
`

const InnerEdgePath = styled.path`
  fill: none;
  stroke-dasharray: 1, 2;
`

const BaseEdgePath = styled.path`
  fill: none;
  stroke-dasharray: 10, 2;

  stroke-width: 15;
  stroke-opacity: ${({ isActive, isHovered }) =>
    isActive || isHovered ? 0.35 : 0.15};

  animation: ${pathTrailAnim} 0.4s linear infinite;
`

const getLinkKey = (source, target) => `${source}->${target}`

export default function GraphRenderer({
  bounds,
  height = 1000,
  links,
  activeNodeBorderScale = 1,
  nodes,
  nodeTextFormatter = n => n.id,
  onDataSelected,
  onDataHovered,
  paletteKey = "secondary",
  tweenDuration = 1.6,
  width = 1000
}) {
  const svgRef = useRef(null)
  const theme = useTheme()
  const id = useId()
  const [activeLinkIndex, setActiveLinkIndex] = useState(-1)
  const [activeAreaIndex, setActiveAreaIndex] = useState(-1)
  const [, setActiveNodeIndex] = useState([])
  const [activeHoverIndex, setActiveHoverIndex] = useState(-1)
  const [interactionsLocked, setInteractionsLocked] = useState(false)
  const [, setErrors] = useState([])
  const [focusBounds, setFocusBounds] = useState(null)
  const [hittableAreas, setHittableAreas] = useState([])
  const viewBoxPadding = width * 0.2

  const { nodePositions, linkPositions, dataDict } = useMemo(() => {
    // create map of node positions
    const nodePositionsDict = {}
    const dataDict = {}
    let newLinkPositions = null
    if (nodes && nodes.length) {
      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i]
        nodePositionsDict[n.id] = [n.x, n.y]
        dataDict[n.id] = n.data
      }

      // calculate link positions, now node positions are known
      newLinkPositions = links.map((l, i) => {
        const sourcePosition = nodePositionsDict[l.source.id]
        const targetPosition = nodePositionsDict[l.target.id]
        const step = (targetPosition[0] - sourcePosition[0]) / 8
        const linkKey = getLinkKey(l.source.id, l.target.id)
        dataDict[linkKey] = l.data
        const pathData = generateArmBetweenPoints({
          source: sourcePosition,
          target: targetPosition,
          step,
          curved: false
        })
        const midPoint = [
          sourcePosition[0] + (targetPosition[0] - sourcePosition[0]) / 2,
          sourcePosition[1] + (targetPosition[1] - sourcePosition[1]) / 2
        ]
        return {
          pathData,
          midPoint,
          sourcePosition,
          targetPosition,
          source: l.source,
          target: l.target
        }
      })
    }
    return {
      nodePositions: nodePositionsDict,
      linkPositions: newLinkPositions,
      dataDict
    }
  }, [nodes, links])

  /**
   * Calculate hittable areas using voronoi diagram
   * constructed from node and mid link positions
   */
  const calculateHittableAreas = useCallback(() => {
    if (nodes && nodes.length && linkPositions && linkPositions.length) {
      const nodePoints = nodes.map(({ x, y }) => [x, y])
      const midLinkPoints = linkPositions.map(({ midPoint }) => midPoint)

      const allPointData = [...nodePoints, ...midLinkPoints]

      const delaunay = Delaunay.from(allPointData)
      const padding = viewBoxPadding * 0.2
      const voronoi = delaunay.voronoi([
        bounds.x[0] - padding,
        bounds.y[0] - padding,
        bounds.x[1] + padding,
        bounds.y[1] + padding
      ])
      const res = Array.from(voronoi.cellPolygons())

      if (res && res.length) {
        setHittableAreas(res)
      }
    }
  }, [bounds, linkPositions, nodes, viewBoxPadding])

  const calculateViewBox = useCallback(
    (bounds) => {
      if (!bounds || !("x" in bounds)) {
        return ""
      }

      const horizontalBounds = bounds.x
      const verticalBounds = bounds.y

      if (verticalBounds[0] > verticalBounds[1]) {
        addError("verticalBounds wrong way around")
        return
      }

      const viewBoxWidth = horizontalBounds[1] - horizontalBounds[0]
      const viewBoxHeight = verticalBounds[1] - verticalBounds[0]
      const viewBox = `${horizontalBounds[0] - viewBoxPadding / 2} ${
        verticalBounds[0] - viewBoxPadding / 2
      } ${viewBoxWidth + viewBoxPadding} ${viewBoxHeight + viewBoxPadding}`
      return viewBox
    },
    [viewBoxPadding]
  )

  useEffect(() => {
    calculateHittableAreas()
  }, [nodePositions, linkPositions, calculateHittableAreas])

  useEffect(() => {
    if (!svgRef.current) {
      return
    }
    const newViewbox = calculateViewBox(
      focusBounds ? focusBounds.bounds : bounds
    )
    const currentViewbox = svgRef.current.getAttribute("viewBox")

    if (newViewbox === currentViewbox) {
      return
    }

    // tween viewport
    TweenLite.fromTo(
      svgRef.current,
      {
        attr: { viewBox: currentViewbox }
      },
      {
        attr: { viewBox: newViewbox },
        ease: "power2.out",
        duration: tweenDuration,
        onComplete: handleViewboxTweenComplete,
        onStart: handleViewboxTweenStart,
        onStartParams: [setInteractionsLocked],
        onCompleteParams: [setInteractionsLocked],
        overwrite: true
      }
    )
  }, [focusBounds, bounds, tweenDuration, calculateViewBox])

  function addError(msg) {
    setErrors(errs => [...errs, msg])
  }
  // function clearErrors() {
  //   setErrors([])
  // }

  function handleViewboxTweenComplete(setInteractionsLocked) {
    if (typeof setInteractionsLocked === "function") {
      setInteractionsLocked(false)
    }
  }
  function handleViewboxTweenStart(setInteractionsLocked) {
    if (typeof setInteractionsLocked === "function") {
      setInteractionsLocked(true)
    }
  }

  function handleHittableAreaMouseLeave(e) {
    if (interactionsLocked) {
      return
    }
    const i = Number.parseInt(e.target.getAttribute("data-index"), 10)
    if (i === activeHoverIndex) {
      setActiveHoverIndex(-1)
      if (onDataHovered && typeof onDataHovered === "function") {
        onDataHovered(null)
      }
    }
  }

  function getDataFromHittableAreaIndex(i) {
    // get data attached to node or link
    const isNode = i < nodes.length

    if (isNode) {
      const node = nodes[i]
      if (node.id in dataDict) {
        return {
          type: "node",
          id: node.id,
          data: dataDict[node.id]
        }
      }
    } else {
      const link = links[i - nodes.length]
      const linkKey = getLinkKey(link.source.id, link.target.id)
      return {
        type: "link",
        source: link.source.id,
        target: link.target.id,
        data: {
          source: dataDict[link.source.id],
          target: dataDict[link.target.id],
          link: dataDict[linkKey]
        }
      }
    }
  }

  function handleHittableAreaMouseEnter(e) {
    if (interactionsLocked) {
      return
    }
    const i = Number.parseInt(e.target.getAttribute("data-index"), 10)
    setActiveHoverIndex(i)

    if (onDataHovered && typeof onDataHovered === "function") {
      const data = getDataFromHittableAreaIndex(i)
      onDataHovered(data)
    }
  }

  function handleHittableAreaTapped(e) {
    if (interactionsLocked) {
      console.log("interactions locked")
      return
    }
    const i = Number.parseInt(e.target.getAttribute("data-index"), 10)

    const tappedAreaAleadyActive = i === activeAreaIndex

    if (tappedAreaAleadyActive) {
      setActiveNodeIndex([])
      setActiveLinkIndex(-1)
      setActiveAreaIndex(-1)
      setFocusBounds(null)

      if (onDataSelected && typeof onDataSelected === "function") {
        onDataSelected(null)
      }
      return
    }

    // map to node or links
    // index same as order nodes and links were passed into Delaunay.from
    // nodes, then links
    const isNode = i < nodes.length
    const res = {
      type: "node",
      data: null
    }
    if (isNode) {
      res.type = "node"
      res.data = nodes[i]
      setActiveNodeIndex([i])
      setActiveLinkIndex(-1)

      setFocusBounds(() => ({
        bounds: getFocusBounds(res.type, [res.data.x, res.data.y]),
        activeNodeIds: [res.data.id]
      }))
    } else {
      res.type = "link"
      res.data = linkPositions[i - nodes.length]
      setActiveLinkIndex(i - nodes.length)

      setFocusBounds(() => ({
        bounds: getFocusBounds(res.type, [
          res.data.sourcePosition,
          res.data.targetPosition
        ]),
        activeNodeIds: [
          links[i - nodes.length].source.id,
          links[i - nodes.length].target.id
        ]
      }))

      const sourceIndex = _findIndex(
        nodes,
        n => n.id === linkPositions[i - nodes.length].source
      )
      const targetIndex = _findIndex(
        nodes,
        n => n.id === linkPositions[i - nodes.length].target
      )

      setActiveNodeIndex([sourceIndex, targetIndex])
    }

    setActiveAreaIndex(i)

    if (onDataSelected && typeof onDataSelected === "function") {
      const data = getDataFromHittableAreaIndex(i)
      onDataSelected(data)
    }
  }

  function getFocusBounds(type, positions) {
    if (type === "node") {
      // single point, so subtract and add padding to make a viewport around it
      return {
        x: [positions[0] - width * 0.2, positions[0] + width * 0.2],
        y: [positions[1] - width * 0.2, positions[1] + width * 0.2]
      }
    } else {
      // find [smallest/largest] [x/y] of both points
      const minX = _minBy(positions, p => p[0])[0]
      const maxX = _maxBy(positions, p => p[0])[0]
      const minY = _minBy(positions, p => p[1])[1]
      const maxY = _maxBy(positions, p => p[1])[1]
      return {
        x: [minX, maxX],
        y: [minY, maxY]
      }
    }
  }

  const viewBox = useMemo(
    () => calculateViewBox(bounds),
    [bounds, calculateViewBox]
  )

  return (
    <GraphWrap style={{ width, height }}>
      {/* <Typography variant="h1">
        interactionsLocked: {interactionsLocked ? "YES" : "NO"}
      </Typography> */}
      {nodes && nodes.length && "x" in nodes[0] && bounds && viewBox ? (
        <Svg ref={svgRef} viewBox={viewBox} width={width} height={height}>
          <g>
            {Object.keys(nodePositions).length
              ? linkPositions.map(({ midPoint, pathData }, i) => {
                  const link = links[i]
                  const linkColor =
                    link.data &&
                    link.data.color &&
                    isValidColor(link.data.color)
                      ? link.data.color
                      : theme.palette[paletteKey].main
                  return (
                    <g key={`edge-${i}`}>
                      <BaseEdgePath
                        stroke={linkColor}
                        d={pathData}
                        isActive={i === activeLinkIndex}
                        isHovered={i === activeHoverIndex - nodes.length}
                      />
                      <InnerEdgePath
                        key={`edge-${i}`}
                        stroke={
                          i === activeLinkIndex
                            ? theme.basePalette.fg
                            : linkColor
                        }
                        d={pathData}
                        markerEnd={`url(#arrow-head-${id})`}
                        isActive={i === activeLinkIndex}
                      />
                      {/* <circle
                        cx={midPoint[0]}
                        cy={midPoint[1]}
                        r={20}
                        fill={theme.basePalette.fg}
                        fillOpacity={0.04}
                      /> */}
                    </g>
                  )
                })
              : ""}
          </g>
          <g>
            {nodes.map((n, i) => (
              <CustomNode
                parentId={id}
                id={n.id}
                index={i}
                activeNodeBorderScale={activeNodeBorderScale}
                key={`node-${n.id}`}
                x={n.x}
                y={n.y}
                node={n}
                nodeTextFormatter={nodeTextFormatter}
                isActive={
                  focusBounds && focusBounds.activeNodeIds.includes(n.id)
                }
                isOnlyItemActive={
                  focusBounds &&
                  focusBounds.activeNodeIds.length === 1 &&
                  focusBounds.activeNodeIds.includes(n.id)
                }
                paletteKey={paletteKey}
              />
            ))}
          </g>
          <g>
            {hittableAreas.map((points, i) => (
              <polygon
                key={`hittable-area-${i}`}
                points={points}
                fill={"black"}
                fillOpacity={0}
                stroke={theme.palette[paletteKey].main}
                strokeOpacity={0}
                data-index={i}
                onClick={handleHittableAreaTapped}
                onMouseEnter={handleHittableAreaMouseEnter}
                onMouseLeave={handleHittableAreaMouseLeave}
              />
            ))}
          </g>
          <defs>
            <marker
              id={`arrow-head-${id}`}
              viewBox="0 0 10 10"
              refX="36"
              refY="5"
              markerUnits="strokeWidth"
              markerWidth="9"
              markerHeight="9"
              orient="auto"
            >
              <path
                d="M 0 0 L 10 5 L 0 10 z"
                fill={theme.palette[paletteKey].main}
              />
            </marker>
          </defs>
        </Svg>
      ) : (
        ""
      )}
    </GraphWrap>
  )
}
